import find from 'lodash/find';
import Deferred from '@Utils/Deferred';
import { messageBrokerConfig } from '@Constants/CommonConstants';
import CriticalRequestUrls from '@Utils/CriticalRequestUrls';
import Catalyst from '@Services/Catalyst';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { poorNetworkConnectionToastMessage } from '@Containers/ToastMessages/PoorNetToastMessage/PoorNetToastMessageActions';
import MessageBrokerQueue from './MessageBrokerQueue';
import MessageBrokerRequest from './MessageBrokerRequest';

const {
  CONNECTION_LIMIT,
  RETRY_DELAYS,
  STATUS: { PENDING, LIMIT_REACHED, FINISHED },
} = messageBrokerConfig;
const queue = new MessageBrokerQueue(CONNECTION_LIMIT);

function isCritical(request) {
  const result = find(CriticalRequestUrls, (urlRequestInstance) => {
    return urlRequestInstance.urlRegex.test(request.url);
  });

  return Boolean(result);
}

function showCriticalRetryLimitErrorMessage(request) {
  Logger.error(`MessageBroker error: retry amount limit to ${request.url} exceeded.`);
  Catalyst.trackError(
    {
      message: `Your http connection to the server was interrupted. `,
      // eslint-disable-next-line no-template-curly-in-string
      longMessage: 'Failed request url: ${request.url}',
      errorNumber: LIMIT_REACHED,
      component: 'XmlHttpRequest',
    },
    'se-'
  );

  NotifyDialogsService.openModalWindow({
    title: 'Server connection interrupted',
    text: `Your http connection to the server was interrupted.\n
           We have tried several times to retrieve the data, but are unable to maintain the connection.\n
           Please contact your IT department for support.\n
           Failed request url: ${request.url}`,
    type: 'error',
    size: 'md',
    confirmText: 'OK, got it',
  });
}

function handleLimitReached(request) {
  if (isCritical(request)) {
    showCriticalRetryLimitErrorMessage(request);
  } else {
    poorNetworkConnectionToastMessage();
  }
}

function sendRequest(request) {
  if (!request) return;

  const next = () => {
    queue.release(request);
    return sendRequest(queue.next());
  };

  const handleFailure = (reason) => {
    switch (reason) {
      case PENDING: {
        const currentDelay = RETRY_DELAYS[request.count - 1];
        const delay = request.time() > currentDelay ? 0 : currentDelay;
        setTimeout(() => sendRequest(request), delay);
        break;
      }
      case FINISHED: {
        next();
        break;
      }
      case LIMIT_REACHED: {
        handleLimitReached(request);
        next();
        break;
      }
      default: {
        Logger.error('MessageBroker send request error: unknown reason', reason);
      }
    }
  };

  request.send().then(next).catch(handleFailure);
}

/**
 * Performs repeatable (in case of failures) http requests
 * @param {*} requestUrl – with url, method and other params
 * @param {Object} [payload] - data
 * @param {Object} [options] - request headers
 * @param {Object} [body] - request body for POST requests to pass in the request body, where payload is populated in query params
 * @returns {Promise} – promise
 */
function send(requestConfig, payload, options, body) {
  const defer = Deferred();
  const request = new MessageBrokerRequest(requestConfig, payload, options, defer, body);

  Object.assign(defer.promise, {
    cancel: () => {
      request.cancel();
      queue.release(request);
      sendRequest(queue.next());
    },
  });

  queue.push(request);
  sendRequest(queue.next());

  return defer.promise;
}

/**
 * Cancels and clears all requests from the queue
 */
function cancelAll() {
  queue.each((request) => {
    request.cancel();
    queue.release(request);
  });
}

export { send, cancelAll };
